import { useState } from "react";

import { useNavigate } from "react-router-dom";

import { es } from "date-fns/locale";
import dayjs from "dayjs";
import { CalendarIcon, Download, Plus, Search } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  useAssemblyLines,
  useProductionBatchStatistics,
  useProductionBatches,
} from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { cn } from "~/lib/utils";
import { exportProductionBatchesToExcel } from "~/services/production/productionBatchExportService";
import type { ProductionBatchStatus } from "~/types/production";
import { PRODUCTION_BATCH_STATUS_LABELS } from "~/types/production";

import { BatchStatisticsCards } from "./components/BatchStatisticsCards";
import { ProductionBatchesTable } from "./components/ProductionBatchesTable";

const DATE_PRESETS = [
  {
    label: "Hoy",
    getRange: () => ({
      start: dayjs().startOf("day"),
      end: dayjs().endOf("day"),
    }),
  },
  {
    label: "Esta semana",
    getRange: () => ({
      start: dayjs().startOf("week"),
      end: dayjs(),
    }),
  },
  {
    label: "Este mes",
    getRange: () => ({
      start: dayjs().startOf("month"),
      end: dayjs(),
    }),
  },
  {
    label: "Mes anterior",
    getRange: () => ({
      start: dayjs().subtract(1, "month").startOf("month"),
      end: dayjs().subtract(1, "month").endOf("month"),
    }),
  },
];

export function ProductionBatchesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isProduction } = useCurrentUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [lineFilter, setLineFilter] = useState<string>("all");
  const [plantFilter, setPlantFilter] = useState<string>("all");
  const [isExporting, setIsExporting] = useState(false);
  const [startDate, setStartDate] = useState<Date>(() =>
    dayjs().startOf("month").toDate()
  );
  const [endDate, setEndDate] = useState<Date>(() => dayjs().toDate());
  const [activePreset, setActivePreset] = useState<string | null>("Este mes");

  const filters = {
    search: searchTerm || undefined,
    status:
      statusFilter !== "all"
        ? (statusFilter as ProductionBatchStatus)
        : undefined,
    assembly_line: lineFilter !== "all" ? lineFilter : undefined,
    plant: plantFilter !== "all" ? plantFilter : undefined,
    start_date_from: startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : undefined,
    start_date_to: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
  };

  const handlePresetClick = (preset: (typeof DATE_PRESETS)[0]) => {
    const { start, end } = preset.getRange();
    setStartDate(start.toDate());
    setEndDate(end.toDate());
    setActivePreset(preset.label);
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      setStartDate(date);
      setActivePreset(null);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      setEndDate(date);
      setActivePreset(null);
    }
  };

  const { data: batches = [], isLoading } = useProductionBatches(filters);
  const { data: assemblyLines = [] } = useAssemblyLines();
  const { data: stats } = useProductionBatchStatistics({
    start_date_from: startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : undefined,
    start_date_to: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
  });

  const handleCreateBatch = () => {
    navigate("/production/batches/new");
  };

  const handleViewBatch = (batchId: string) => {
    navigate(`/production/batches/${batchId}`);
  };

  const handleExport = () => {
    setIsExporting(true);
    try {
      exportProductionBatchesToExcel(batches);
      toast({
        title: "Exportación exitosa",
        description: `Se exportaron ${batches.length} lotes de producción.`,
      });
    } catch (error) {
      console.error("Error exporting batches:", error);
      toast({
        title: "Error en la exportación",
        description: "No se pudo generar el archivo Excel.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Lotes de Producción"
        description="Gestión de lotes de producción semanal"
        actions={
          !isProduction ? (
            <Button onClick={handleCreateBatch} className="h-9">
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Lote
            </Button>
          ) : undefined
        }
      />

      {stats && (
        <BatchStatisticsCards
          stats={stats}
          activeStatus={statusFilter !== "all" ? statusFilter : undefined}
          onStatusClick={setStatusFilter}
        />
      )}

      <Card>
        <CardHeader className="space-y-4 pb-4">
          {/* Row 1: Period presets + date range pickers */}
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <span className="shrink-0 text-sm font-medium text-muted-foreground">
              Período:
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  variant={
                    activePreset === preset.label ? "default" : "outline"
                  }
                  size="sm"
                  className="h-9"
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:ml-2 sm:border-l sm:pl-4">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-9 w-[130px] justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate
                      ? dayjs(startDate).format("DD/MM/YYYY")
                      : "Desde"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={handleStartDateChange}
                    weekStartsOn={1}
                    locale={es}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground">—</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-9 w-[130px] justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? dayjs(endDate).format("DD/MM/YYYY") : "Hasta"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={handleEndDateChange}
                    weekStartsOn={1}
                    locale={es}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Row 2: Filters + search + export */}
          <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <Select value={lineFilter} onValueChange={setLineFilter}>
                <SelectTrigger className="h-9 w-full sm:w-auto sm:min-w-[110px]">
                  <SelectValue placeholder="Línea" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las líneas</SelectItem>
                  {assemblyLines.map((line) => (
                    <SelectItem key={line.id} value={line.code}>
                      {line.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={plantFilter} onValueChange={setPlantFilter}>
                <SelectTrigger className="h-9 w-full sm:w-auto sm:min-w-[110px]">
                  <SelectValue placeholder="Planta" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las plantas</SelectItem>
                  <SelectItem value="P1">P1</SelectItem>
                  <SelectItem value="P2">P2</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-full sm:w-auto sm:min-w-[140px]">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {Object.entries(PRODUCTION_BATCH_STATUS_LABELS).map(
                    ([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>

              <div className="relative w-full sm:w-auto sm:min-w-[200px]">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-9 w-full pl-9"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 sm:shrink-0">
              <span className="text-sm text-muted-foreground">
                {batches.length} lotes
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                onClick={handleExport}
                disabled={isExporting || isLoading || batches.length === 0}
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? "Exportando..." : "Exportar Excel"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ProductionBatchesTable
            batches={batches}
            isLoading={isLoading}
            onViewBatch={handleViewBatch}
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
}
